// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Build-time feature flags. CRA bakes REACT_APP_* into the bundle at build
// time, so flipping one of these is a rebuild, not a runtime toggle.

import { LD_FLAGS, type LDFlagName } from './launchDarklyFlags';

/**
 * Surfaces that are only fit to be shown locally — built out enough to
 * develop against, but not ready to be seen by anyone outside the team
 * (incomplete, scaffolded, or backed by data we don't want to present yet).
 *
 * Defaults to **off**, so a deployed/demo build hides them unless someone
 * explicitly opts in. Turn it on for local development in `front/.env.local`:
 *
 *     REACT_APP_DISPLAY_LOCAL_ONLY=true
 *
 * Gating the surfaces themselves is applied in two places, and both matter:
 *   - `navModel.ts` drops the rail items, so the surfaces aren't reachable
 *     by clicking.
 *   - `AppRoutes.tsx` redirects their routes, so a stale bookmark or a typed
 *     URL can't reach them either. Hiding only the nav would leave the page
 *     one URL away, which is not "hidden".
 * (A third reader, `FeatureFlagDebugPanel.tsx`, checks this flag too — but
 * to gate its own visibility, not a product surface. It doesn't count
 * against "two places", which is specifically about hiding a surface.)
 */
export const DISPLAY_LOCAL_ONLY = process.env.REACT_APP_DISPLAY_LOCAL_ONLY === 'true';

/**
 * LaunchDarkly client-side ID for the stage-1 pilot (AB#29033 / wiki 2463).
 * Unset by default — LaunchDarklyProvider skips initializing the SDK
 * entirely when this is empty, so every flag falls back to its declared
 * default (fail closed) instead of the app depending on a LaunchDarkly key
 * to boot. Set in `front/.env.local` for local testing:
 *
 *     REACT_APP_LAUNCHDARKLY_CLIENT_ID=<client-side id from the LD project>
 */
export const LAUNCHDARKLY_CLIENT_SIDE_ID = process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID ?? '';

// The LaunchDarkly flags themselves live in config/launchDarklyFlags.ts and
// are read with hooks/useFeatureFlag.ts, not this file — those are runtime
// flags with a catalog and a default, not a build-time REACT_APP_* constant.

/**
 * Per-environment fallback for one catalog flag, read when LaunchDarkly has
 * no boolean for it (flag not created, LaunchDarkly unreachable or not
 * configured). LaunchDarkly always wins when it answers; only a flag absent
 * from both LaunchDarkly and the environment reads the catalog default (off).
 *
 *     REACT_APP_FLAG_<flag key in SCREAMING_SNAKE_CASE>=true|false
 *
 * Set in `front/.env.local` locally, or in the deploy workflow's build env
 * per environment — CRA bakes it in at build time like every REACT_APP_*.
 */
export function envFlagDefault(name: LDFlagName): boolean | undefined {
  const value =
    process.env[`REACT_APP_FLAG_${LD_FLAGS[name].key.toUpperCase().replace(/-/g, '_')}`];
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

/**
 * Show every digital twin entity type instead of the curated asset list —
 * local debugging only. Set in `front/.env.local`:
 *
 *     REACT_APP_SHOW_ALL_ENTITY_TYPES=true
 */
export const SHOW_ALL_ENTITY_TYPES = process.env.REACT_APP_SHOW_ALL_ENTITY_TYPES === 'true';
